// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

// import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'
import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'
import useApplyFilters from '@aytm/app/stats/page/sidebar/components/hooks/useApplyFilters'

import { removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'
import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'

// components
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { RadioButton } from 'primereact/radiobutton'
import { Slider } from 'primereact/slider'

import SelectOverlay from '@aytm/app/stats/page/sidebar/components/SelectOverlay'
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const THRESHOLD_OPTIONS = [
  { key: '0.2', text: '20%' },
  { key: '0.25', text: '25%' },
  { key: '0.333', text: '33.3%' },
  { key: '0.5', text: '50%' },
  { key: '0.6', text: '60%' },
  { key: '0.7', text: '70%' },
  { key: '0.8', text: '80%' }
]
const DEFAULT_THRESHOLD_OPTION = '0.7'

const DEFAULT_ITEMS_PER_SET = 4

const EXPORT_TYPES = [
  'turf',
  'conjoint',
  'emd',
  'ec',
  'concept_lab',
  'concept_mapping',
  'tracker_lab',
  'tracker_mapping'
]

// a mapping-mode question exports the same workbook as its Lab; the export service has no mapping writer
const LAB_EXPORT_TYPES = { concept_mapping: 'concept_lab', tracker_mapping: 'tracker_lab' }

const SpecificOptions = ({ report, options, exportType, processing, disabled, settings, onExport }) => {
  const onExportClick = useCallback(
    event => {
      'function' == typeof onExport &&
        onExport(
          {
            fileType: options.fileType,
            exportType: options.exportType,
            options: {
              ...options.exportOptions,
              method: options.key
            }
          },
          event
        )
    },
    [options, onExport]
  )

  return (
    <>
      {settings}
      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid={`stat-exports-${exportType}-export`}
            className="p-button-rounded p-button-blue"
            label={EXPORT_TYPES.includes(processing) ? 'Downloading' : 'Download file'}
            loading={EXPORT_TYPES.includes(processing)}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

SpecificOptions.propTypes = {
  report: PropTypes.object,
  options: PropTypes.object,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const SpecificMdOptions = ({
  root,
  report,
  features,
  question,
  options,
  exportType,
  processing,
  disabled,
  settings,
  onExport
}) => {
  const [method, setMethod] = useState(features.special_export.config?.turf?.default || options[0].key)
  const [threshold, setThreshold] = useState(null)

  const thresholdRef = useRef(null)
  thresholdRef.current = threshold

  const methodObj = useMemo(() => options.find(option => option.key == method) || null, [method, options])

  const onExportClick = useCallback(
    event => {
      'function' == typeof onExport &&
        onExport(
          {
            fileType: methodObj.fileType,
            exportType: methodObj.exportType,
            options: { ...methodObj.exportOptions, method, threshold }
          },
          event
        )
    },
    [methodObj, method, threshold, onExport]
  )

  useEffect(
    () =>
      methodObj.exportOptions.threshold
        ? setThreshold(null === thresholdRef.current ? DEFAULT_THRESHOLD_OPTION : thresholdRef.current)
        : setThreshold(null),
    [methodObj]
  )

  return (
    <>
      <div className={style.section}>
        {options.map((item, index) => (
          <div key={`${item.key}-${index}`} className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid={`stat-exports-${exportType}-method-${item.key}`}
                className="p-radiobutton-sm"
                aria-labelledby={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                value={item.key}
                checked={method == item.key}
                disabled={disabled}
                onChange={disabled ? null : () => setMethod(item.key)}
              />
              <span
                data-testid={`stat-exports-${exportType}-method-${item.key}-label`}
                id={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': disabled
                })}
                onClick={disabled ? null : () => setMethod(item.key)}
              >
                {item.text}
              </span>
            </div>
          </div>
        ))}

        {features.special_export.config?.turf?.help && root ? (
          <SidebarPopup
            id="export"
            root={root}
            help={features.special_export.config?.turf?.help.replace(
              '{{itemsPerSet}}',
              question.items_per_set || DEFAULT_ITEMS_PER_SET
            )}
            width="300px"
          />
        ) : null}

        {methodObj.exportOptions.threshold && null !== threshold ? (
          <div className={`${style.row} ${style['row-indent']} ${style['row-slider']}`}>
            <div className="p-fluid p-col-12">
              <Slider
                data-testid={`stat-exports-${exportType}-threshold`}
                min={0}
                max={THRESHOLD_OPTIONS.length - 1}
                value={THRESHOLD_OPTIONS.findIndex(value => value.key == threshold) || 0}
                labelTemplate={value => THRESHOLD_OPTIONS[value].text}
                disabled={disabled}
                onChange={disabled ? null : event => setThreshold(THRESHOLD_OPTIONS[event.value].key)}
              />
            </div>
          </div>
        ) : null}
      </div>

      {settings}

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid={`stat-exports-${exportType}-export`}
            className="p-button-rounded p-button-blue"
            label={EXPORT_TYPES.includes(processing) ? 'Downloading' : 'Download file'}
            loading={EXPORT_TYPES.includes(processing)}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

SpecificMdOptions.propTypes = {
  root: PropTypes.any,
  report: PropTypes.object,
  features: PropTypes.object,
  question: PropTypes.object,
  options: PropTypes.array,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const SpecificTurfOptions = ({ report, options, exportType, processing, disabled, settings, onExport }) => {
  const [method, setMethod] = useState(options[0].key)

  const methodObj = useMemo(() => options.find(option => option.key == method) || null, [method, options])

  const onExportClick = useCallback(
    event => {
      'function' == typeof onExport &&
        onExport(
          {
            fileType: methodObj.fileType,
            exportType: methodObj.exportType,
            options: { ...methodObj.exportOptions, method }
          },
          event
        )
    },
    [methodObj, method, onExport]
  )

  return (
    <>
      <div className={style.section}>
        {options.map((item, index) => (
          <div key={`${item.key}-${index}`} className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid={`stat-exports-${exportType}-method-${item.key}`}
                className="p-radiobutton-sm"
                aria-labelledby={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                value={item.key}
                checked={method == item.key}
                disabled={disabled}
                onChange={disabled ? null : () => setMethod(item.key)}
              />
              <span
                data-testid={`stat-exports-${exportType}-method-${item.key}-label`}
                id={`stat-exports-${exportType}-method-${item.key}-${index}-label`}
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': disabled
                })}
                onClick={disabled ? null : () => setMethod(item.key)}
              >
                {item.text}
              </span>
            </div>
          </div>
        ))}
      </div>

      {settings}

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid={`stat-exports-${exportType}-export`}
            className="p-button-rounded p-button-blue"
            label={EXPORT_TYPES.includes(processing) ? 'Downloading' : 'Download file'}
            loading={EXPORT_TYPES.includes(processing)}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

SpecificTurfOptions.propTypes = {
  report: PropTypes.object,
  options: PropTypes.array,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const Specific = ({
  root,
  survey,
  model,
  data,
  report,
  features,
  exportType,
  processing,
  disabled,
  onExport,
  onParamsChange
}) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const [exportQuestion, setExportQuestion] = useState(null)

  // the same two settings every other category carries, saved the same way: the logic setting as the
  // report's shared export param, the filter tick in the sidebar against the filter it was set under
  const [clExportVisibility, setClExportVisibility] = useState('hide' == model.clExportVisibility)
  const [applyFilters, setApplyFilters] = useApplyFilters(sidebarState.export?.specific, model.filtersPattern)

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: { specific: { applyFilters, filtersPattern: model.filtersPattern } }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applyFilters, model.filtersPattern]
  )

  useNonInitialEffect(
    () => onParamsChange?.({ cl_export_visibility: clExportVisibility ? 'hide' : 'show' }, false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clExportVisibility, onParamsChange]
  )

  const exportQuestionRef = useRef(null)
  exportQuestionRef.current = exportQuestion

  // the dropdown shows one question per line and filters on `search`; authoring markup would both
  // restyle the line and break a match that spans the tag
  // the page replaces a category's list when the report changes it but keeps the object holding the
  // lists, so the list itself is what the options follow
  const exportList = data[exportType]
  const exportQuestionsOptions = useMemo(
    () =>
      (exportList || []).map(option => ({
        ...option,
        text: removeTagsFromText(option.text),
        search: removeTagsFromText(option.search)
      })),
    [exportList]
  )

  const exportQuestionObj = useMemo(() => {
    const option = exportQuestionsOptions.find(option => option.key == exportQuestion)
    if (option == undefined) return null

    return option
  }, [exportQuestion, exportQuestionsOptions])

  const exportQuestionOptions = useMemo(() => {
    if (null === exportQuestionObj) return null

    const exportOptionsDefaults = {
      key: exportQuestionObj.key,
      link: exportQuestionObj.link || null,
      position: exportQuestionObj.position
    }

    if ('cb' == exportQuestionObj.type) {
      return {
        key: exportType,
        fileType: 'binary',
        exportType: 'turf',
        exportOptions: {
          ...exportOptionsDefaults,
          turfType: 'answers'
        }
      }
    }

    if (['emd', 'cl', 'tl'].includes(exportQuestionObj.type)) {
      return {
        key: exportType,
        fileType: 'emd' == exportQuestionObj.type ? 'csv' : 'xlsx',
        exportType: LAB_EXPORT_TYPES[exportType] || exportType,
        exportOptions: {
          ...exportOptionsDefaults
        }
      }
    }

    if ('ec' == exportQuestionObj.type) {
      return [
        {
          key: 'question',
          text: 'Raw Events',
          fileType: 'csv',
          exportType: 'question',
          exportOptions: { ...exportOptionsDefaults }
        }
      ]
    }

    if ('cjc' == exportQuestionObj.type) {
      return [
        {
          key: 'conjointPkgs',
          text: 'Ranked List of Packages',
          fileType: 'csv',
          exportType: 'conjointPkgs',
          exportOptions: { ...exportOptionsDefaults }
        },
        {
          key: 'conjointDesign',
          text: 'Design Sheet by aytm',
          fileType: 'csv',
          exportType: 'conjointDesign',
          exportOptions: { ...exportOptionsDefaults }
        }
      ]
    }

    if ('cjd' == exportQuestionObj.type) {
      return [
        {
          key: 'conjoint',
          text: 'Raw Utility Scores',
          fileType: 'csv',
          exportType: 'conjoint',
          exportOptions: { ...exportOptionsDefaults }
        },
        {
          key: 'conjointPkgs',
          text: 'Ranked List of Packages',
          fileType: 'csv',
          exportType: 'conjointPkgs',
          exportOptions: { ...exportOptionsDefaults }
        },
        {
          key: 'conjointDesign',
          text: 'Design Sheet by aytm',
          fileType: 'csv',
          exportType: 'conjointDesign',
          exportOptions: { ...exportOptionsDefaults }
        }
      ]
    }

    if ('md' == exportQuestionObj.type) {
      return Object.entries(features.special_export.config?.turf?.available).map(([key, value]) => {
        return {
          key,
          text: value.text.replace('{{itemsPerSet}}', exportQuestionObj.items_per_set || DEFAULT_ITEMS_PER_SET),
          fileType: value.fileType,
          exportType: 'turf',
          exportOptions: {
            ...exportOptionsDefaults,
            threshold: value.threshold || false,
            methodText: value.xlsxText.replace(
              '{{itemsPerSet}}',
              exportQuestionObj.items_per_set || DEFAULT_ITEMS_PER_SET
            )
          }
        }
      })
    }

    const options = []
    const exportOptions = {
      ...exportOptionsDefaults,
      turfType: 'batches'
    }
    // A not-applicable answer (positive: false) is never a box, so positions count the substantive answers only.
    const answerVariants = exportQuestionObj.batches[0].answerVariants.filter(
      av => !Object.hasOwn(av, 'positive') || av.positive
    )

    options.push({
      key: 'bottom1',
      text: `TURF TOP 1 box (A${answerVariants.length})`,
      fileType: 'binary',
      exportType: 'turf',
      exportOptions: { method: 'top1', positive: [answerVariants.length], ...exportOptions }
    })
    if (3 < answerVariants.length) {
      options.push({
        key: 'bottom2',
        text: `TURF TOP 2 box (A${answerVariants.length - 1} & A${answerVariants.length})`,
        fileType: 'binary',
        exportType: 'turf',
        exportOptions: {
          method: 'top2',
          positive: [answerVariants.length - 1, answerVariants.length],
          ...exportOptions
        }
      })
      options.push({
        key: 'top2',
        text: `TURF TOP 2 box (A1 & A2)`,
        fileType: 'binary',
        exportType: 'turf',
        exportOptions: { method: 'top2', positive: [1, 2], ...exportOptions }
      })
    }
    options.push({
      key: 'top1',
      text: `TURF TOP 1 box (A1)`,
      fileType: 'binary',
      exportType: 'turf',
      exportOptions: { method: 'top1', positive: [1], ...exportOptions }
    })

    return options
  }, [exportType, exportQuestionObj, features.special_export])

  // A title longer than the row is cut short, so the whole of it is carried on the row itself for
  // the reader to hover -- as plain text, since the label it is cut from is authored markup.
  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span title={removeTagsFromText(option.label)} dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span
        title={removeTagsFromText(`${option.key.toUpperCase()}. ${option.text}`)}
        className={option.icon ? 'p-item-label' : ''}
      >
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: `${option.key.toUpperCase()}. ${option.text}` }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon && <i className={`icon-aytmfd-${option.icon}`} />}
        <span dangerouslySetInnerHTML={{ __html: `${option.key.toUpperCase()}. ${option.text}` }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  // a new category starts on its first question; within a category the pick stays on its question
  // while the report keeps it (a drag only reorders), and moves to the first once it is removed
  const pickedTypeRef = useRef(exportType)
  useEffect(() => {
    const typeChanged = pickedTypeRef.current !== exportType
    pickedTypeRef.current = exportType
    setExportQuestion(current =>
      !typeChanged && exportQuestionsOptions.some(option => option.key == current)
        ? current
        : (exportQuestionsOptions[0]?.key ?? null)
    )
  }, [exportType, exportQuestionsOptions])

  // auto-force a fresh export for filtered conjoint — cached results would be stale across filter changes
  const onExportClick = useCallback(
    (payload, event) =>
      onExport({
        ...payload,
        props: {
          ...payload.props,
          clExportVisibility,
          applyFilters,
          // a Shift-click asks for a fresh export, as on every other category
          noCache:
            Boolean(event?.shiftKey) ||
            (0 < model.filtersPattern.length && ['maxdiff', 'conjoint'].includes(payload.exportType))
        }
      }),
    [model.filtersPattern, clExportVisibility, applyFilters, onExport]
  )

  // the export's settings sit above its download button, as in every other category
  const settings =
    survey?.cl_exists || 0 < model.filtersPattern.length ? (
      <div className={style.section}>
        {survey?.cl_exists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-specific-cl-export-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-specific-cl-export-visibility-label"
                value="cl-export-visibility"
                checked={clExportVisibility}
                disabled={disabled}
                onChange={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setClExportVisibility(!clExportVisibility))
                }
              />
              <span
                id="stat-exports-specific-cl-export-visibility-label"
                data-testid="stat-exports-specific-cl-export-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
              >
                Hide aytm Logic
              </span>
            </div>
          </div>
        ) : null}
        {0 < model.filtersPattern.length ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-specific-apply-filters"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-specific-apply-filters-label"
                value="apply-filters"
                checked={applyFilters}
                disabled={disabled}
                onChange={disabled ? null : () => setApplyFilters(!applyFilters)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setApplyFilters(!applyFilters))}
              />
              <span
                id="stat-exports-specific-apply-filters-label"
                data-testid="stat-exports-specific-apply-filters-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setApplyFilters(!applyFilters)}
              >
                Apply active filters
              </span>
            </div>
          </div>
        ) : null}
      </div>
    ) : null

  return (
    <>
      {0 < exportQuestionsOptions.length ? (
        'turf' == exportType && features.turf.disabled ? (
          <div className="p-component p-button p-button-rounded p-button-light-blue p-disabled">
            <i className="icon-aytmfd-lock"></i>
            <span className="p-button-label p-c">Select a question</span>
          </div>
        ) : (
          <div className={style.row}>
            {1 < exportQuestionsOptions.length ? (
              <div className="p-fluid p-col-12">
                <SelectOverlay
                  data-testid={`stat-exports-${exportType}-question`}
                  className="p-dropdown-rounded p-dropdown-light-blue"
                  placeholder="Select a question"
                  value={exportQuestion}
                  options={exportQuestionsOptions}
                  itemTemplate={itemTemplate}
                  valueTemplate={valueTemplate}
                  filter
                  filterBy="search,text"
                  filterPlaceholder="Search items"
                  disabled={disabled}
                  onChange={setExportQuestion}
                />
              </div>
            ) : (
              <div className="p-fluid p-col-12">
                <div
                  data-testid={`stat-exports-${exportType}-question`}
                  className="p-component p-button p-button-rounded p-button-light-blue p-disabled"
                >
                  {exportQuestionsOptions[0].icon && <i className={`icon-aytmfd-${exportQuestionsOptions[0].icon}`} />}
                  <span
                    className="p-button-label p-c"
                    dangerouslySetInnerHTML={{
                      __html: `${exportQuestionsOptions[0].key.toUpperCase()}. ${exportQuestionsOptions[0].text}`
                    }}
                    style={{ display: 'inline-block', textAlign: 'left' }}
                  ></span>
                </div>
              </div>
            )}
          </div>
        )
      ) : null}

      {null !== exportQuestionObj ? (
        ['cb', 'emd', 'cl', 'tl'].includes(exportQuestionObj.type) ? (
          <SpecificOptions
            report={report}
            options={exportQuestionOptions}
            exportType={exportType}
            processing={processing}
            disabled={disabled}
            settings={settings}
            onExport={onExportClick}
          />
        ) : 'md' == exportQuestionObj.type ? (
          <SpecificMdOptions
            root={root}
            report={report}
            features={features}
            question={exportQuestionObj}
            options={exportQuestionOptions}
            exportType={exportType}
            processing={processing}
            disabled={disabled}
            settings={settings}
            onExport={onExportClick}
          />
        ) : (
          <SpecificTurfOptions
            report={report}
            options={exportQuestionOptions}
            exportType={exportType}
            processing={processing}
            disabled={disabled}
            settings={settings}
            onExport={onExportClick}
          />
        )
      ) : null}
    </>
  )
}

Specific.propTypes = {
  root: PropTypes.any,
  survey: PropTypes.object,
  model: PropTypes.object,
  data: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  exportType: PropTypes.string,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func,
  onParamsChange: PropTypes.func
}

Specific.displayName = 'Specific'

export default Specific
